import {FormProvider, useForm} from "react-hook-form";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {useTranslation} from "react-i18next";
import {Button, Grid, useTheme,} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {useRecoilState} from "recoil";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import SectionHeader from "../../../../../util/component/SectionHeader";
import {Box} from "@mui/system";
import {
    FormProductCodeLocationGroupRateAssociation,
    ProductCodeLocationGroupRateAssociation,
    RateProductCodeLocationGroupDTO,
} from "../../../api/types/rateProductCodeLocationGroup";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE,} from "../../../api/types/commercialCategory";
import {
    fromGenericBeToGenericFe,
    fromRateBeToFeForRatePlan,
    fromSingleGenericBeToSingleGenericFe,
} from "../../../utils";
import {
    FormDialog,
    SelectElementModelBE,
    SelectElementModelFE,
    UseLocationStateModel,
} from "../../../api/types/generic";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {productCodesAtom} from "../../../state/locationGroupProductCodesRecoil";
import CardContainer from "../../../common/components/CardContainer";
import {useProductCodeRepository} from "../../../api/repositories/ProductCodeRepository";
import {FormAutocomplete} from "../../../common/form-fields";
import {locationGroupAtom} from "../../../state/locationGroupsRecoil";
import {useProductCodeLocationGroupRepository} from "../../../api/repositories/ProductCodeLocationGroupRepository";
import {useLocationGroupRepository} from "../../../api/repositories/LocationGroupRepository";
import {useLocation, useNavigate} from "react-router-dom";
import {rateSelectListAtom} from "../../../state/ratesRecoil";
import {useRateRepository} from "../../../api/repositories/RateRepository";
import {RateEntityResponse, RateSelectFE} from "../../../api/types/rate";
import {useRateProductCodeLocationGroupRepository} from "../../../api/repositories/RateProductCodeLocationGroup";
import {ProductCodeSelectFE} from "../../../api/types/productCode";
import {AggregateLocationGroupByRate} from "../../../api/types/aggregateLocationGroupByRate";
import Loading from "../../../common/components/Loading";
import {LocationGroupEntityResponse} from "../../../api/types/locationGroup";
import FieldArray from "./fieldArray";
import DealerAndTenant from "../../../common/components/DealerAndTenant";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const RateProductCodeLocationGroupForm = ({
                                              readOnly,
                                          }: {
    readOnly: boolean;
}) => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    const {globalData: {user}} = useContext(GlobalContext);

    // Use Navigate
    const navigate = useNavigate();

    // Use Location
    const location = useLocation();

    const locationState: UseLocationStateModel =
        location.state as UseLocationStateModel;

    //PER CHI ARRIVA DA SALES&MARKETING, NAVIGARE SU QUESTA PAGINA INSERENDO NELLO STATE DELLA PAGINA IL PRODUCT CODE
    //navigate("/pricing/product-code/location-group-rate/create", {replace: true, state: {productCodeId: "0000000022"}});

    // Use State
    const [formDialog] = useState<FormDialog | null>(null);
    const [initialValues] = useState<FormProductCodeLocationGroupRateAssociation>(
        {
            commercialCategory: null,
            commercialSegmentId: null,
            productCode: null,
            associations: [],
            dealer: null,
            legalEntity: null,
        }
    );
    const [oldAssociations, setOldAssociations] = useState<
        RateProductCodeLocationGroupDTO[]
    >([]);

    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [productCodeList, setProductCodeList] = useRecoilState(productCodesAtom);
    const [rateList, setRateList] = useRecoilState(rateSelectListAtom);
    const [locationGroupList, setLocationGroupList] = useRecoilState(locationGroupAtom);

    // Use Repository Hooks
    const {getCommercialSegments} = useCommercialSegmentRepository();
    const {
        getCommercialCategories,
        getCommercialCategoriesByCommercialSegmentId
    } = useCommercialCategoryRepository();
    const {getProductCode, getProductCodesByCommercialCategoryId} = useProductCodeRepository();
    const {getRateAssociations, getLocationGroupsByProductCode} = useProductCodeLocationGroupRepository();
    const {getLocationGroups, getLocationGroup} = useLocationGroupRepository();
    const {getRatesList} = useRateRepository();
    const {postRateProductCodeLocationGroupAll} = useRateProductCodeLocationGroupRepository();
    const [updating, setUpdating] = useState(false);
    const [loadingUpdate, setLoadingUpdate] = useState(false);

    const handleGetStaticContent = async () => {
        // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
        // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] =
            fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    // ************ FORM - START ************ //
    const validationSchema = yup
        .object()
        .shape({
            dealer: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable(true),
            legalEntity: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            commercialSegmentId: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            commercialCategoryId: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            productCode: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            associations: yup
                .array()
                .of(
                    yup.object().shape({
                        locationGroup: yup
                            .array()
                            .of(
                                yup.object().shape({
                                    code: yup.string(),
                                    label: yup.string(),
                                })
                            )
                            .min(1, t("pricing:validation.minOne"))
                            .nullable()
                            .required(t("translation:validation.fieldMandatory")),
                        rate: yup
                            .array()
                            .of(
                                yup.object().shape({
                                    code: yup.string(),
                                    label: yup.string(),
                                })
                            )
                            .min(1, t("pricing:validation.minOne"))
                            .nullable()
                            .required(t("translation:validation.fieldMandatory")),
                    })
                )
                .nullable()
                .min(0),
        })
        .defined();

    // Use Form
    const form = useForm<FormProductCodeLocationGroupRateAssociation>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema,
        ),
        shouldUnregister: true
    });
    const {
        control,
        register,
        getValues,
        formState: {errors},
        setValue
    } = form;
    const watchAllForm = form.watch();
    // ************ FORM - END ************ //

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
    }, []);

    useEffect(() => {
        //atterraggio con codice prodotto già selezionato
        const handleGetContent = async () => {
            if (form && locationState?.productCodeId) {
                //ottenere product code da id
                const productCodeEntityResponse = await getProductCode(
                    String(locationState?.productCodeId)
                );
                setValue("legalEntity", {
                    code: productCodeEntityResponse.legalEntity as string,
                    label:  t('translation:legalEntities.'.concat(productCodeEntityResponse.legalEntity as string))
                })
                setValue("dealer", {
                    code: productCodeEntityResponse.dealer as string,
                    label:  t('translation:dealers.'.concat(productCodeEntityResponse.dealer as string))
                })

                //selezionare segmento commerciale corrispondente a categoria commerciale in product code
                const commercialSegment: SelectElementModelFE =
                    fromSingleGenericBeToSingleGenericFe(
                        productCodeEntityResponse.commercialCategory.commercialSegment
                    );
                commercialSegment &&
                form.setValue("commercialSegmentId", commercialSegment);

                //ottenere lista di categorie commerciali del segmento commerciale
                const commercialCategoriesData =
                    await getCommercialCategoriesByCommercialSegmentId(
                        commercialSegment?.code as string
                    );
                const commercialCategoryList: CommercialCategorySelectFE[] =
                    fromGenericBeToGenericFe(commercialCategoriesData);
                setCommercialCategoryList(commercialCategoryList);

                //selezionare categoria commerciale di product code
                const commercialCategory: CommercialCategorySelectFE =
                    fromSingleGenericBeToSingleGenericFe(
                        productCodeEntityResponse.commercialCategory
                    );
                commercialCategory &&
                form.setValue("commercialCategory", commercialCategory);

                //ottenere lista di product code da categoria commerciale
                const productCodesData = await getProductCodesByCommercialCategoryId(
                    commercialCategory?.code as string,  user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string || productCodeEntityResponse.dealer as string,
                    user.legalEntities.length === 1 ? user.legalEntities[0].name as string : watchAllForm.legalEntity?.code as string || productCodeEntityResponse.legalEntity as string
                );
                const productCodeList: ProductCodeSelectFE[] =
                    fromGenericBeToGenericFe(productCodesData);
                setProductCodeList(productCodeList);

                const ratesData = (
                    await getRatesList({
                        commercialCategoryId: commercialCategory?.code,
                        showOnlyActive: true,
                        legalEntities: user.legalEntities.length === 1 ? user.legalEntities[0].name as string :  watchAllForm.legalEntity?.code as string || productCodeEntityResponse.legalEntity as string,
                        dealers: user.dealers.length === 1 ? user.dealers[0].name as string : watchAllForm.dealer?.code as string || productCodeEntityResponse.dealer as string,
                    })
                );
                setRateList([
                    {
                        code: 0,
                        label: t("pricing:actions.selectAll"),
                    },
                    ...fromRateBeToFeForRatePlan(ratesData),
                ]);

                //selezionare product code
                const productCode: ProductCodeSelectFE | undefined =
                    productCodeList.find(
                        (pc) => pc.code === productCodeEntityResponse.id
                    );
                productCode && form.setValue("productCode", productCode);

                //ottenere lista di gruppi di location legati a product code
                const locationGroupsData = await getLocationGroupsByProductCode(productCode?.code as string);
                const locationGroupList: SelectElementModelFE[] = fromGenericBeToGenericFe(locationGroupsData);
                setLocationGroupList([{code: 0, label: t("pricing:actions.selectAll")}, ...locationGroupList]);
            }
        };
        handleGetContent().then(() => {
            setUpdating(false);
            setLoadingUpdate(false)
        });

    }, [locationState?.productCodeId, updating]);

    useEffect(() => {
        const handleGetContent = async () => {
            try {
                const productCodeId = watchAllForm?.productCode?.code as string;
                // const legalEntities = globalData.user.legalEntity.map(
                //     (d: any) => d.name
                // );
                if (productCodeId) {
                    //get di associazioni già esistenti
                    const associations = await getRateAssociations(
                        productCodeId,
                        user.legalEntities.length === 1 ? user.legalEntities[0].name as string : (watchAllForm.legalEntity as SelectElementModelFE)?.code as string
                    );
                    if (associations.length === 0 && locationState?.locationGroupId) {
                        const locationGroup = await getLocationGroup(
                            locationState?.locationGroupId
                        );
                        associations.push({
                            locationGroups: [locationGroup],
                            rates: [],
                        });
                    }
                    if (associations.length > 0) {
                        const productCodeLocationGroupRateAssociation: ProductCodeLocationGroupRateAssociation[] =
                            associations
                                //filtrare associazioni già esistenti se è dato un gruppo di location in locationState
                                .filter((association) => {
                                    return locationState?.locationGroupId
                                        ? association.locationGroups.find(
                                            (lg) => lg.id === Number(locationState?.locationGroupId)
                                        )
                                        : true;
                                })
                                .map((association) => ({
                                    locationGroup: fromGenericBeToGenericFe(
                                        association.locationGroups
                                    )
                                        //filtrare gruppi di location nella card se è dato un gruppo di location in locationState
                                        .filter((el) => {
                                            return locationState?.locationGroupId
                                                ? el.code === Number(locationState?.locationGroupId)
                                                : true;
                                        }),
                                    rate: fromRateBeToFeForRatePlan(association.rates),
                                }));
                        form.setValue(
                            "associations",
                            productCodeLocationGroupRateAssociation
                        );

                        //creazione lista di associazioni già esistenti
                        const oldAssociations: RateProductCodeLocationGroupDTO[] = [];

                        associations.forEach(
                            (association: AggregateLocationGroupByRate) => {
                                association.locationGroups.forEach(
                                    (locationGroup: LocationGroupEntityResponse) => {
                                        association.rates.forEach((rate: RateEntityResponse) => {
                                            oldAssociations.push({
                                                productCodeId,
                                                locationGroupId: locationGroup.id,
                                                rateId: rate.id,
                                            });
                                        });
                                    }
                                );
                            }
                        );

                        setOldAssociations(oldAssociations);
                    } else {
                        handleAddCard();
                    }
                }
            } catch (e) {

            }
        };

        handleGetContent().then(() => {
            setUpdating(false);
            setLoadingUpdate(false)
        });
    }, [watchAllForm?.productCode, updating]);

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        if (value) {
            form.setValue("commercialSegmentId", value);
            const commercialCategoriesData =
                await getCommercialCategoriesByCommercialSegmentId(
                    (value as SelectElementModelFE).code as string
                );
            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            form.setValue("commercialCategory", null);
            form.setValue("productCode", null);
            setCommercialCategoryList(commercialCategoryList);
        } else {
            form.setValue("commercialSegmentId", value);
            // TODO: togliere COMMERCIAL_CATEGORIES || quando si pusha
            // const commercialCategoriesData: CommercialCategoryEntityResponse[] = COMMERCIAL_CATEGORIES || await getCommercialCategories();
            const commercialCategoriesData: CommercialCategoryEntityResponse[] =
                await getCommercialCategories();

            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            form.setValue("commercialCategory", null);
            form.setValue("productCode", null);
            setValue("associations", []);

            setCommercialCategoryList(commercialCategoryList);
        }
    };

    const handleChangeCommercialCategory = async (
        value: CommercialCategorySelectFE
    ) => {
        if (value) {
            form.setValue("commercialCategory", value);
            const productCodesData = await getProductCodesByCommercialCategoryId(
                (value as CommercialCategorySelectFE).code as string, user.dealers.length === 1 ? user.dealers[0].name as string : (watchAllForm.dealer as SelectElementModelFE)?.code as string, user.legalEntities.length === 1 ? user.legalEntities[0].name as string : (watchAllForm.legalEntity as SelectElementModelFE)?.code as string
            );
            const productCodeList: ProductCodeSelectFE[] = productCodesData.map(
                (product) => ({
                    code: product.id,
                    label: product.id + " - " + product.name,
                    commercialCategory: product.commercialCategory,
                    active: product.active,
                })
            );
            form.setValue("productCode", null);
            setProductCodeList(productCodeList);

            const ratesData = (
                await getRatesList({
                    commercialCategoryId: (value as CommercialCategorySelectFE).code,
                    showOnlyActive: true,
                    dealers: user.dealers.length === 1 ? user.dealers[0].name as string : (watchAllForm.dealer as SelectElementModelFE)?.code as string,
                    legalEntities: user.legalEntities.length === 1 ? user.legalEntities[0].name as string : (watchAllForm.legalEntity as SelectElementModelFE)?.code as string,
                })
            );
            setRateList([
                {
                    code: 0,
                    label: t("pricing:actions.selectAll"),
                },
                ...fromRateBeToFeForRatePlan(ratesData),
            ]);
        } else {
            form.setValue("commercialCategory", value);
            form.setValue("productCode", null);
            form.setValue("associations", []);

            setProductCodeList([]);
            setRateList([]);
        }
    };

    const handleChangeProductCode = async (value: SelectElementModelFE) => {
        if (value) {
            form.setValue("productCode", value);
            const locationGroupsData = await getLocationGroupsByProductCode((value as CommercialCategorySelectFE).code as string);
            const locationGroupList: SelectElementModelFE[] = fromGenericBeToGenericFe(locationGroupsData);
            setLocationGroupList([{code: 0, label: t("pricing:actions.selectAll")}, ...locationGroupList]);
        } else {
            form.setValue("productCode", value);
            const locationGroupsData: SelectElementModelBE[] = await getLocationGroups(user.legalEntities.length === 1 ? user.legalEntities[0].name as string : (watchAllForm.legalEntity as SelectElementModelFE)?.code as string);
            const locationGroupList: SelectElementModelFE[] = fromGenericBeToGenericFe(locationGroupsData);
            setValue("associations", []);
            setOldAssociations([]);
            setLocationGroupList([{code: 0, label: t("pricing:actions.selectAll")}, ...locationGroupList]);
        }
    };

    const handleChangeLocationGroup = async (
        value: SelectElementModelFE[],
        index: number
    ) => {
        const currentValue: SelectElementModelFE[] =
            // è stato cliccato "selectAll
            value.find(
                (el: SelectElementModelFE) =>
                    el.label === t("pricing:actions.selectAll")
            ) &&
            // tra i vecchi valori non c'è "selectAll
            !getValues().associations[index].locationGroup?.find(
                (el: SelectElementModelFE) =>
                    el.label === t("pricing:actions.selectAll")
            )
                ? // applica il "selectAll
                locationGroupList.filter(
                    (el: SelectElementModelFE) =>
                        el.label !== t("pricing:actions.selectAll")
                )
                : // setta solo i car group selezionati
                value.filter(
                    (el: SelectElementModelFE) =>
                        el.label !== t("pricing:actions.selectAll")
                );

        const currentAssociations = form.getValues().associations;
        currentAssociations.splice(index, 1, {
            ...currentAssociations[index],
            locationGroup: currentValue,
        });
        setValue("associations", currentAssociations);
    };

    const handleChangeRate = async (value: RateSelectFE[], index: number) => {
        const currentValue: RateSelectFE[] =
            // è stato cliccato "selectAll
            value.find(
                (el: RateSelectFE) => el.label === t("pricing:actions.selectAll")
            ) &&
            // tra i vecchi valori non c'è "selectAll
            !getValues().associations[index].rate?.find(
                (el: RateSelectFE) => el.label === t("pricing:actions.selectAll")
            )
                ? // applica il "selectAll
                rateList.filter(
                    (el: RateSelectFE) => el.label !== t("pricing:actions.selectAll")
                )
                : // setta solo i car group selezionati
                value.filter(
                    (el: RateSelectFE) => el.label !== t("pricing:actions.selectAll")
                );

        const currentAssociations = getValues().associations;
        currentAssociations.splice(index, 1, {
            ...currentAssociations[index],
            rate: currentValue,
        });
        setValue("associations", currentAssociations);
    };

    const handleAddCard = () => {
        const associations = form.getValues().associations;
        let emptyCard: ProductCodeLocationGroupRateAssociation | undefined =
            associations?.find(
                (association: ProductCodeLocationGroupRateAssociation) =>
                    association.locationGroup?.length === 0
            );

        if (associations?.length === 0 || !emptyCard) {
            emptyCard = {
                locationGroup: [],
                rate: [],
            };
            const currentAssociations = form.getValues().associations;
            currentAssociations.push(emptyCard);
            setValue("associations", currentAssociations);
        }
    };

    const handleConfirm = async () => {
        setLoadingUpdate(true)
        const productCodeId = watchAllForm?.productCode?.code as string;
        const currentAssociationsArray: RateProductCodeLocationGroupDTO[] = [];

        //costruire array da eliminare
        getValues().associations.forEach((association) => {
            association.locationGroup.forEach((lg: SelectElementModelFE) => {
                association.rate.forEach((rate: RateSelectFE) => {
                    const currentAssociation: RateProductCodeLocationGroupDTO = {
                        productCodeId,
                        locationGroupId: Number(lg.code),
                        rateId: Number(rate.code),
                    };
                    currentAssociationsArray.push(currentAssociation);
                });
            });
        });

        // let difference = arr1.filter(x => !arr2.includes(x));
        // includes === arr2.filter(e => e.locationGroupId === x.locationGroupId).length > 0

        //current - old => associazioni da inserire
        const associazioniDaInserire: RateProductCodeLocationGroupDTO[] =
            currentAssociationsArray.filter((ca) => {
                return !(
                    oldAssociations.filter((oa) => {
                        return (
                            oa.rateId === ca.rateId &&
                            oa.locationGroupId === ca.locationGroupId
                        );
                    }).length > 0
                );
            });

        //old - current => associazioni da togliere
        const associazioniDaEliminare: RateProductCodeLocationGroupDTO[] =
            oldAssociations.filter((oa) => {
                return !(
                    currentAssociationsArray.filter((ca) => {
                        return (
                            ca.rateId === oa.rateId &&
                            ca.locationGroupId === oa.locationGroupId
                        );
                    }).length > 0
                );
            });

        await postRateProductCodeLocationGroupAll({
            rateProductCodeLocationGroupEntityRequest: {
                rateProductCodeLocationGroupToSave: associazioniDaInserire,
                rateProductCodeLocationGroupToDelete: associazioniDaEliminare,
            },
        }).then(() => {
            setUpdating(true)
        });
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_RATE_PLAN_CREATE.name")}/>

            <FormProvider {...form}>
                <CardContainer>
                    <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                        <DealerAndTenant
                            readOnly={readOnly}
                            md={3}
                            legalEntityName={"legalEntity"}
                            dealerName={"dealer"}
                            dealerIsVisible={true}
                        />
                        {/* COMMERCIAL SEGMENTS */}
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...form.register("commercialSegmentId")}
                                label={`${t("pricing:rateProductCodeLocationGroup.form.labels.commercialSegment")}*`}
                                options={commercialSegmentList}
                                isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                readOnly={Boolean(formDialog?.readonly) || Boolean(formDialog?.inUse)}
                                handleChange={handleChangeCommercialSegment}
                            />
                        </Grid>

                        {/* COMMERCIAL CATEGORY */}
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...form.register("commercialCategory")}
                                label={`${t("pricing:rateProductCodeLocationGroup.form.labels.commercialCategory")}*`}
                                options={commercialCategoryList}
                                isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                readOnly={!watchAllForm.commercialSegmentId || Boolean(formDialog?.readonly) || Boolean(formDialog?.inUse)}
                                handleChange={handleChangeCommercialCategory}
                            />
                        </Grid>

                        {/* PRODUCT CODE */}
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...form.register("productCode")}
                                label={`${t("pricing:rateProductCodeLocationGroup.form.labels.productCode")}*`}
                                options={productCodeList}
                                isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                                readOnly={!watchAllForm.commercialCategory || Boolean(formDialog?.readonly) || Boolean(formDialog?.inUse)}
                                handleChange={handleChangeProductCode}
                            />
                        </Grid>
                    </Grid>
                </CardContainer>

                <FieldArray
                    {...{
                        control,
                        register,
                        defaultValues: initialValues,
                        getValues,
                        setValue,
                        errors,
                        formDialog,
                        handleChangeRate,
                        rateList,
                        handleChangeLocationGroup,
                        locationGroupList,
                        label: getValues()?.productCode?.label || "",
                        loadingUpdate: loadingUpdate
                    }}
                />

                {!Boolean(formDialog?.readonly) &&
                    <Grid container item xs={12} justifyContent="flex-end" columnGap={2} p={2}>
                        <Grid>
                            <Button variant="outlined"
                                    sx={{marginRight: "15px"}}
                                    color={"accent"}
                                    type={"button"}
                                    disabled={form.formState.isSubmitting}
                                    onClick={() => {
                                        navigate("../product-code/location-group-rate/list");
                                    }}
                            >
                                {t("pricing:actions.back")}
                            </Button>
                            <Button color={"accent"}
                                    type={"button"}
                                    onClick={form.handleSubmit(handleConfirm)}
                                    disabled={form.formState.isSubmitting}
                            >
                                {t("pricing:actions.confirm")}
                            </Button>
                        </Grid>
                    </Grid>
                }
            </FormProvider>

            <Loading/>
        </Box>
    );
};

export default RateProductCodeLocationGroupForm;
